import React, { useState } from 'react';
import {
    LIMIT_SCENARIO_IDS,
    getNumericScenarioDefault,
    isNumericScenario,
    PLAN_OVERRIDE_OPTIONS,
    normaliseNumericValue,
} from '../../dev-scenarios/catalog';
import {
    setDevLimitScenario,
    getActiveDevScenario,
    setDevPlanOverride,
    getActiveDevPlanOverride,
    reloadWithoutLimitScenarioQuery,
} from '../../dev-scenarios/browser-state';

const GREEN = '#30cf43';
const INK = '#15171a';
const MUTED = '#7c8b96';
const LINE = '#edf0f2';

const styles = {
    panel: {
        position: 'fixed',
        right: '20px',
        top: '20px',
        zIndex: 40,
        display: 'flex',
        flexDirection: 'column',
        width: '260px',
        padding: '12px 14px',
        borderRadius: '12px',
        border: `1px solid ${LINE}`,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        boxShadow: '0 2px 6px rgba(15, 23, 26, 0.04), 0 12px 32px rgba(15, 23, 26, 0.10)',
        fontFamily: 'inherit',
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    label: {
        margin: 0,
        color: MUTED,
        fontSize: '1.05rem',
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        flex: 1,
        whiteSpace: 'nowrap',
    },
    switchWrap: {
        position: 'relative',
        display: 'inline-flex',
        flexShrink: 0,
        cursor: 'pointer',
    },
    switchInput: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        opacity: 0,
        margin: 0,
        cursor: 'pointer',
    },
    switchTrack: (isOn) => ({
        width: '36px',
        height: '21px',
        borderRadius: '21px',
        background: isOn ? GREEN : '#dde2e6',
        transition: 'background 0.18s ease',
        pointerEvents: 'none',
    }),
    switchKnob: (isOn) => ({
        position: 'absolute',
        top: '2.5px',
        left: isOn ? '17.5px' : '2.5px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(15, 23, 26, 0.25)',
        transition: 'left 0.18s ease',
        pointerEvents: 'none',
    }),
    expandButton: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '22px',
        height: '22px',
        background: 'none',
        border: 'none',
        borderRadius: '6px',
        padding: 0,
        cursor: 'pointer',
        color: MUTED,
        flexShrink: 0,
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        marginTop: '10px',
        paddingTop: '8px',
        borderTop: `1px solid ${LINE}`,
        maxHeight: '300px',
        overflowY: 'auto',
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        padding: '5px 6px',
        borderRadius: '6px',
        fontSize: '1.3rem',
        color: INK,
    },
    itemLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        flex: 1,
        minWidth: 0,
        // Match the row's size explicitly: the app's global `label` style would
        // otherwise override the inherited size on this nested label
        fontSize: '1.3rem',
        color: INK,
        cursor: 'pointer',
        userSelect: 'none',
    },
    itemCheckbox: {
        accentColor: GREEN,
        width: '14px',
        height: '14px',
        margin: 0,
        cursor: 'pointer',
        flexShrink: 0,
    },
    numberInput: {
        width: '74px',
        padding: '3px 6px',
        borderRadius: '5px',
        border: `1px solid #d3d8dc`,
        fontSize: '1.2rem',
        color: INK,
        flexShrink: 0,
    },
};

/**
 * Chevron icon for the expand/collapse control.
 *
 * @param {{up: boolean}} props
 * @returns {JSX.Element}
 */
function Chevron({ up }) {
    return (
        <svg
            width="10"
            height="7"
            viewBox="0 0 10 7"
            fill="none"
            style={{
                transform: up ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.18s ease',
            }}
        >
            <path
                d="M1 1.5l4 4 4-4"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * Floating dev-only picker for blocked-plan limit scenarios. Renders nothing
 * outside the local dev server. Lives inside the BMA itself because the BMA
 * iframe's storage is partitioned by the embedding admin, so the latch must
 * be set from within the iframe's own context.
 *
 * Switch OFF = override disabled, real site state. Switch ON with nothing
 * checked = neutral baseline (nothing blocked). Flag scenarios are simple
 * checkboxes; numeric scenarios (staff/newsletters/automations/members) add an integer
 * input so any count can be simulated. Selections combine automatically.
 *
 * @returns {JSX.Element|null}
 */
export default function DevLimitScenarioPanel() {
    const [expanded, setExpanded] = useState(false);
    const [planExpanded, setPlanExpanded] = useState(false);

    if (process.env.NODE_ENV !== 'development') {
        return null;
    }

    const { on: isOn, flags, numeric } = getActiveDevScenario();

    const activePlan = getActiveDevPlanOverride();
    const planIsOn = activePlan !== null;

    // Serialise the current selection (plus an edit) into a latch and reload.
    // An empty selection while the switch is on holds the neutral baseline.
    const commit = (nextFlags, nextNumeric) => {
        const tokens = [
            ...nextFlags,
            ...Object.keys(nextNumeric).map((id) => `${id}=${nextNumeric[id]}`),
        ];
        setDevLimitScenario(tokens.length ? tokens.join(',') : 'none');
        reloadWithoutLimitScenarioQuery();
    };

    const handleSwitch = (event) => {
        setDevLimitScenario(event.target.checked ? 'none' : '');
        reloadWithoutLimitScenarioQuery();
    };

    const handleToggleFlag = (id) => {
        const nextFlags = flags.includes(id) ? flags.filter((flag) => flag !== id) : [...flags, id];
        commit(nextFlags, numeric);
    };

    const handleToggleNumeric = (id) => {
        const nextNumeric = { ...numeric };
        if (id in nextNumeric) {
            delete nextNumeric[id];
        } else {
            nextNumeric[id] = getNumericScenarioDefault(id);
        }
        commit(flags, nextNumeric);
    };

    const handleNumericValue = (id, rawValue) => {
        const value = normaliseNumericValue(id, rawValue);
        if (numeric[id] === value) {
            return; // unchanged — avoid a needless reload
        }
        commit(flags, { ...numeric, [id]: value });
    };

    const handlePlanSwitch = (event) => {
        setDevPlanOverride(event.target.checked ? PLAN_OVERRIDE_OPTIONS[0].id : '');
        reloadWithoutLimitScenarioQuery();
    };

    const handleSelectPlan = (plan) => {
        setDevPlanOverride(plan);
        reloadWithoutLimitScenarioQuery();
    };

    const renderScenarioItem = (id) => {
        const isNumeric = isNumericScenario(id);
        const isChecked = isNumeric ? id in numeric : flags.includes(id);
        return (
            <div style={styles.item} key={id}>
                <label style={styles.itemLabel}>
                    <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() =>
                            isNumeric ? handleToggleNumeric(id) : handleToggleFlag(id)
                        }
                        style={styles.itemCheckbox}
                        data-test-id={`dev-limit-scenario-option-${id}`}
                    />
                    {id}
                </label>
                {isNumeric && isChecked && (
                    <input
                        type="number"
                        min="0"
                        defaultValue={numeric[id]}
                        style={styles.numberInput}
                        onBlur={(event) => handleNumericValue(id, event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.target.blur();
                            }
                        }}
                        data-test-id={`dev-limit-scenario-input-${id}`}
                    />
                )}
            </div>
        );
    };

    return (
        <div style={styles.panel} data-test-id="dev-limit-scenario-panel">
            <div style={styles.row}>
                <p style={styles.label}>Current plan override</p>
                {planIsOn && (
                    <button
                        style={styles.expandButton}
                        onClick={() => setPlanExpanded((current) => !current)}
                        data-test-id="dev-plan-override-expand"
                        aria-label={planExpanded ? 'Collapse plans' : 'Expand plans'}
                    >
                        <Chevron up={planExpanded} />
                    </button>
                )}
                <span style={styles.switchWrap}>
                    <input
                        type="checkbox"
                        checked={planIsOn}
                        onChange={handlePlanSwitch}
                        style={styles.switchInput}
                        data-test-id="dev-plan-override-switch"
                    />
                    <span style={styles.switchTrack(planIsOn)} />
                    <span style={styles.switchKnob(planIsOn)} />
                </span>
            </div>
            {planIsOn && planExpanded && (
                <div style={styles.list}>
                    {PLAN_OVERRIDE_OPTIONS.map(({ id, label }) => (
                        <label key={id} style={styles.item}>
                            <input
                                type="radio"
                                name="dev-plan-override"
                                checked={activePlan === id}
                                onChange={() => handleSelectPlan(id)}
                                style={styles.itemCheckbox}
                                data-test-id={`dev-plan-override-option-${id}`}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            )}
            <div
                style={{
                    ...styles.row,
                    marginTop: '10px',
                    paddingTop: '10px',
                    borderTop: `1px solid ${LINE}`,
                }}
            >
                <p style={styles.label}>Plan limits override</p>
                {isOn && (
                    <button
                        style={styles.expandButton}
                        onClick={() => setExpanded((current) => !current)}
                        data-test-id="dev-limit-scenario-expand"
                        aria-label={expanded ? 'Collapse scenarios' : 'Expand scenarios'}
                    >
                        <Chevron up={expanded} />
                    </button>
                )}
                <span style={styles.switchWrap}>
                    <input
                        type="checkbox"
                        checked={isOn}
                        onChange={handleSwitch}
                        style={styles.switchInput}
                        data-test-id="dev-limit-scenario-switch"
                    />
                    <span style={styles.switchTrack(isOn)} />
                    <span style={styles.switchKnob(isOn)} />
                </span>
            </div>
            {isOn && expanded && (
                <div style={styles.list}>{LIMIT_SCENARIO_IDS.map(renderScenarioItem)}</div>
            )}
        </div>
    );
}
